import { Suspense } from "react";
import { Navigate, useParams } from "react-router";
import { PageNotAvailable } from "../shared/components/NotAvailable";
import { ScreenPlaceholder } from "./ScreenPlaceholder";
import { NotFoundPage } from "./NotFoundPage";
import type { ScreenDef } from "./screens";
import { useSession } from "../shared/session/session-context";
import { useProject } from "../shared/api/queries";
import { isApiProblem } from "../shared/api/problem";

/** Applies partner, admin and flag rules before rendering a screen (UX-01 4.8, 4.12). */
export function ScreenRoute({ screen }: { screen: ScreenDef }) {
  const session = useSession();
  const { projectKey } = useParams();
  // A project the caller holds no role on is 404 from the server, identical to a missing one: every project screen
  // then shows the same "not found" page, never the screen's own empty state (project visibility ruling, 1 Oct 2026).
  const project = useProject(screen.path.includes(":projectKey") ? projectKey : undefined);

  if (project.error && isApiProblem(project.error) && project.error.status === 404) {
    return <ProjectNotFound />;
  }
  // Batch 18: a screen whose route needs a flag is "Page not found" while the flag is off, for everyone (it ships dark).
  if (screen.routeFlag && !session.flag(screen.routeFlag)) return <NotFoundPage />;
  if (screen.hideForPartner && session.isPartner) {
    return <PageNotAvailable title="Page not available" reason="This page is not available to partner staff. Ask your TFG contact." />;
  }
  // Settings ruling (2 Oct 2026): SystemAdmin only; in test mode the test identity decides, so the admin is refused too.
  // A page marked readOnlyInTestMode (Settings → Permissions) stays open, read-only, for the SystemAdmin in test mode
  // (`testMode` is present only for a person who may use test mode, a SystemAdmin).
  const readOnlyHere = screen.readOnlyInTestMode === true && session.testMode?.active === true;
  if (screen.requiresSystemAdmin && !session.canOpenSettings && !readOnlyHere) {
    return <PageNotAvailable title={screen.title} reason={settingsRefusal(session)} />;
  }
  // Features__Sprints off: backlog and active sprint land on the Kanban board, which explains why (UX-01 4.12).
  if (screen.navFlag === "Sprints" && !session.flag("Sprints") && projectKey) {
    return <Navigate to={`/p/${encodeURIComponent(projectKey)}/kanban`} replace />;
  }
  const Component = screen.Component;
  if (!Component) return <ScreenPlaceholder screen={screen} />;
  return (
    <Suspense
      fallback={
        <div className="gf-loading" role="status">
          Loading…
        </div>
      }
    >
      <Component />
    </Suspense>
  );
}

/** Why a Settings page is refused: test mode (the admin can turn it off) or not a SystemAdmin. */
function settingsRefusal(session: Pick<ReturnType<typeof useSession>, "testMode">): string {
  return session.testMode?.active
    ? "Settings are for a System Admin only, and test mode is on. Turn off test mode to open settings."
    : "Settings are for a System Admin only. Ask a System Admin to make this change.";
}

/** Wording depends only on the response, never on why: missing and not shared are the same 404. */
function ProjectNotFound() {
  return <PageNotAvailable title="Project not found" reason="This project does not exist, or you do not have access to it." />;
}
